/**
 * NumberSeriesTable Component
 *
 * One row per agenda with its saved formatting and the number it would issue next.
 */
import { Button, Icon, SimpleTable } from '@insio/ui-toolkit';
import type { NumberSeriesFieldsFragment } from '@/graphql';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface NumberSeriesTableProps {
  rows: readonly NumberSeriesFieldsFragment[];
  loading: boolean;
  error?: unknown;
  onEdit: (row: NumberSeriesFieldsFragment) => void;
}

export function NumberSeriesTable({ rows, loading, error, onEdit }: NumberSeriesTableProps) {
  const t = useFeatureTranslation('systemSettings.numberSeries');
  const tDocumentType = useEnumTranslation('numberSeriesDocumentType');

  if (loading && rows.length === 0) {
    return (
      <div className='py-xl text-center'>
        <p className='text-sm text-foreground-muted'>{t('table.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='py-xl text-center'>
        <p className='text-sm text-error-text'>{t('table.error')}</p>
      </div>
    );
  }

  const headers = [
    t('fields.documentType'),
    t('fields.prefix'),
    t('fields.pattern'),
    t('fields.padding'),
    t('fields.preview'),
    <span key='actions-header' className='flex justify-center'>
      {t('fields.actions')}
    </span>
  ];

  return (
    <SimpleTable<NumberSeriesFieldsFragment>
      testId='number-series-table'
      headers={headers}
      data={[...rows]}
      scrollable
      NoContentFallback={<p className='text-sm text-foreground-muted'>{t('table.empty')}</p>}
      renderRow={row => [
        <span key='documentType' className='text-xs md:text-sm font-medium text-foreground-base'>
          {tDocumentType(row.documentType)}
        </span>,
        <span key='prefix' className='text-xs md:text-sm text-foreground-base'>
          {row.prefix}
        </span>,
        <code
          key='pattern'
          className='text-xs bg-background-input px-sm py-xs rounded font-mono whitespace-nowrap'
        >
          {row.pattern}
        </code>,
        <span key='padding' className='text-xs md:text-sm text-foreground-base'>
          {row.padding}
        </span>,
        <code
          key='preview'
          className='text-xs bg-background-input px-sm py-xs rounded font-mono whitespace-nowrap'
          data-testid={`number-series-preview-${row.documentType}`}
        >
          {row.preview}
        </code>,
        <div key='actions' className='flex justify-center'>
          <Button
            variant='ghost'
            size='sm'
            testId={`number-series-edit-${row.documentType}`}
            aria-label={t('actions.edit')}
            onClick={() => onEdit(row)}
          >
            <Icon name='pen' size='sm' />
          </Button>
        </div>
      ]}
    />
  );
}
